import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';

/**
 * Base composable for CSAT button customisation
 * Returns common button text and styling fields
 * Can be extended by specific button composables for additional fields
 *
 * @param sectionKey - The section key for the specific button (e.g., SKIP_BUTTON, SUBMISSION_BUTTON)
 * @returns Object containing common button fields and getField function for extensions
 */
const useCsatButtonCustomisationBase = (sectionKey: string) => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.CSAT,
    sectionKey,
    blockKey: PUI.CSAT_BLOCK_KEYS.USER_SUGGESTIONS,
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);

  return {
    isVisible,
    buttonText,
    buttonBackgroundColor,
    buttonTextColor,
    buttonBorderColor,
    getField, // Expose getField for extensions
  };
};

/**
 * Composable for CSAT skip button customisation
 * Returns button text and styling
 * Can be extended with skip-specific fields if needed in the future
 *
 * @example
 * // To add skip-specific fields in the future:
 * export const useCsatSkipButtonCustomisation = () => {
 *   const base = useCsatButtonCustomisationBase(PUI.CSAT_SECTION_KEYS.SKIP_BUTTON);
 *   const skipSpecificField = base.getField('skipSpecificField');
 *   const { getField, ...rest } = base;
 *   return { ...rest, skipSpecificField };
 * };
 */
export const useCsatSkipButtonCustomisation = () => {
  const base = useCsatButtonCustomisationBase(PUI.CSAT_SECTION_KEYS.SKIP_BUTTON);
  const { getField, ...rest } = base;
  return rest;
};

/**
 * Composable for CSAT submit button customisation
 * Returns button text and styling
 * Can be extended with submit-specific fields if needed in the future
 *
 * @example
 * // To add submit-specific fields in the future:
 * export const useCsatSubmitButtonCustomisation = () => {
 *   const base = useCsatButtonCustomisationBase(PUI.CSAT_SECTION_KEYS.SUBMISSION_BUTTON);
 *   const disabledBackgroundColor = base.getField(COMMON_FIELD_KEYS.DISABLED_BACKGROUND_COLOR);
 *   const { getField, ...rest } = base;
 *   return { ...rest, disabledBackgroundColor };
 * };
 */
export const useCsatSubmitButtonCustomisation = () => {
  const base = useCsatButtonCustomisationBase(PUI.CSAT_SECTION_KEYS.SUBMISSION_BUTTON);
  const { getField, ...rest } = base;
  return rest;
};
